import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, MultiSearchSelect, Notice, PageHeader, RowActions, Select, Table, Tag } from '@voice/ui';
import { DestinationPicker, errorText, useDirectory } from '../components/common';
import { SpeechField, type Speech } from '../components/SpeechField';
import { api } from '../lib/api';
import type { Destination } from '../lib/types';
import styles from './Pages.module.css';

interface QueueSettings {
  welcome?: Speech;
  periodic?: Speech;
  periodicEverySec?: number;
  announcePosition: boolean;
  announceWaitTime: boolean;
  positionEverySec: number;
  exitKey?: string;
  callbackKey?: string;
  autoPauseAfter: number;
  voicemail?: { greeting?: Speech; email?: string };
  redialAfterDeclineSec?: number;
}
interface Member {
  extensionId: string;
  number: string;
  name: string;
  penalty: number;
  isStatic: boolean;
}
interface Queue {
  id: string;
  name: string;
  number: string | null;
  strategy: string;
  ringTimeoutSec: number;
  wrapupSec: number;
  maxWaitSec: number;
  maxCallers: number;
  joinEmpty: boolean;
  mohClassId: string | null;
  settings: QueueSettings;
  overflow: Destination;
  timeoutDestination: Destination;
  members: Member[];
}
interface Response {
  strategies: string[];
  queues: Queue[];
}

const STRATEGY_LABEL: Record<string, string> = {
  ringall: 'Alle gleichzeitig klingeln',
  linear: 'Der Reihe nach (nach Priorität)',
  leastrecent: 'Wer am längsten nicht dran war',
  fewestcalls: 'Wer die wenigsten Anrufe hatte',
  rrmemory: 'Reihum',
  random: 'Zufällig',
};

const KEY = ['queues'] as const;
const DEST_ALLOW: Destination['type'][] = ['extension', 'flow', 'voicemail', 'queuemail', 'queue', 'hangup'];

export function Queues() {
  const query = useQuery({ queryKey: KEY, queryFn: () => api<Response>('/queues') });
  const [editing, setEditing] = useState<Queue | 'new' | null>(null);
  return (
    <div className={styles.page}>
      <PageHeader title="Warteschlangen">
        <Button onClick={() => setEditing('new')}>Neue Warteschlange</Button>
      </PageHeader>
      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Warteschlangen konnten nicht geladen werden.</Notice>}
      {query.data?.queues.length === 0 && <EmptyState>Noch keine Warteschlange. Anrufer warten dort mit Ansagen und Musik, bis ein Mitarbeiter frei ist.</EmptyState>}
      {query.data && query.data.queues.length > 0 && (
        <Table aria-label="Warteschlangen">
          <thead>
            <tr>
              <th>Name</th>
              <th>Nummer</th>
              <th>Verteilung</th>
              <th>Mitarbeiter</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {query.data.queues.map((q) => (
              <tr key={q.id}>
                <th scope="row">{q.name}</th>
                <td>{q.number ?? <span className={styles.muted}>–</span>}</td>
                <td>{STRATEGY_LABEL[q.strategy] ?? q.strategy}</td>
                <td>{q.members.length === 0 ? <Tag tone="warn">niemand</Tag> : q.members.map((m) => m.number).join(', ')}</td>
                <td>
                  <RowActions>
                    <Button variant="secondary" onClick={() => setEditing(q)}>
                      Bearbeiten
                    </Button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing && query.data && <QueueForm key={editing === 'new' ? 'new' : editing.id} queue={editing === 'new' ? null : editing} strategies={query.data.strategies} onClose={() => setEditing(null)} />}
    </div>
  );
}

const NEW_QUEUE: Omit<Queue, 'id' | 'members'> = {
  name: '',
  number: null,
  strategy: 'leastrecent',
  ringTimeoutSec: 20,
  wrapupSec: 0,
  maxWaitSec: 300,
  maxCallers: 10,
  joinEmpty: false,
  mohClassId: null,
  settings: { announcePosition: false, announceWaitTime: false, positionEverySec: 60, autoPauseAfter: 0, redialAfterDeclineSec: 45, periodicEverySec: 60 },
  overflow: { type: 'hangup' },
  timeoutDestination: { type: 'hangup' },
};

function QueueForm({ queue, strategies, onClose }: { queue: Queue | null; strategies: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const dir = useDirectory();
  const [q, setQ] = useState<Omit<Queue, 'id' | 'members'>>(() => {
    if (!queue) return NEW_QUEUE;
    const st = queue.settings;
    // Ältere Einstellung „Position/Wartezeit ansagen“ wird zur wiederkehrenden Ansage mit Platzhaltern.
    if ((st.announcePosition || st.announceWaitTime) && !st.periodic) {
      const text = [st.announcePosition ? 'Sie sind Anrufer Nummer {{position}} in der Warteschlange.' : '', st.announceWaitTime ? 'Die geschätzte Wartezeit beträgt etwa {{minuten}} {{einheit}}.' : ''].filter(Boolean).join(' ');
      return { ...queue, settings: { ...st, announcePosition: false, announceWaitTime: false, periodic: { text }, periodicEverySec: st.positionEverySec } };
    }
    return queue;
  });
  const [members, setMembers] = useState<{ extensionId: string; penalty: number; isStatic: boolean }[]>((queue?.members ?? []).map((m) => ({ extensionId: m.extensionId, penalty: m.penalty, isStatic: m.isStatic })));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof typeof q>(key: K, value: (typeof q)[K]) => setQ((p) => ({ ...p, [key]: value }));
  const setS = (patch: Partial<QueueSettings>) => setQ((p) => ({ ...p, settings: { ...p.settings, ...patch } }));
  const num = (v: string) => Number(v.replace(/\D/g, '') || 0);

  const body = () => {
    const s = { ...q.settings };
    for (const k of ['welcome', 'periodic'] as const) {
      const v = s[k];
      if (!v || (v.text !== undefined && !v.text.trim()) || (v.audioId !== undefined && !v.audioId)) delete s[k];
    }
    if (!s.exitKey) delete s.exitKey;
    if (!s.callbackKey) delete s.callbackKey;
    if (!s.periodic) delete s.periodicEverySec;
    return { ...q, number: q.number || null, mohClassId: q.mohClassId || null, settings: s };
  };
  const save = useMutation({
    mutationFn: async () => {
      const id = queue ? (await api(`/queues/${queue.id}`, { method: 'PUT', body: body() }), queue.id) : (await api<{ id: string }>('/queues', { method: 'POST', body: body() })).id;
      await api(`/queues/${id}/members`, { method: 'PUT', body: { members } });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/queues/${queue!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });


  return (
    <Modal
      open
      onClose={onClose}
      title={queue ? `Warteschlange „${queue.name}“` : 'Neue Warteschlange'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!q.name || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.row}>
          <Field label="Name" value={q.name} onChange={(e) => set('name', e.target.value)} autoFocus />
          <Field label="Interne Rufnummer (optional)" inputMode="numeric" value={q.number ?? ''} onChange={(e) => set('number', e.target.value.replace(/\D/g, '') || null)} placeholder="z. B. 600" />
        </div>

        <div>
          <h3 className={styles.heading}>Verteilung</h3>
          <div className={styles.row}>
            <Select label="Wer klingelt zuerst?" value={q.strategy} onChange={(e) => set('strategy', e.target.value)}>
              {strategies.map((s) => (
                <option key={s} value={s}>
                  {STRATEGY_LABEL[s] ?? s}
                </option>
              ))}
            </Select>
            <Field label="Klingeln pro Mitarbeiter (Sekunden)" inputMode="numeric" value={String(q.ringTimeoutSec)} onChange={(e) => set('ringTimeoutSec', num(e.target.value))} />
            <Field label="Nachbearbeitung (Sekunden)" inputMode="numeric" value={String(q.wrapupSec)} onChange={(e) => set('wrapupSec', num(e.target.value))} hint="Pause nach einem Gespräch, bevor der nächste Anruf kommt." />
            <Field label="Auto-Pause nach verpassten Anrufen" inputMode="numeric" value={String(q.settings.autoPauseAfter)} onChange={(e) => setS({ autoPauseAfter: num(e.target.value) })} hint="0 = nie." />
            <Field label="Nach Ablehnen erneut klingeln nach (Sekunden)" inputMode="numeric" value={String(q.settings.redialAfterDeclineSec ?? 45)} onChange={(e) => setS({ redialAfterDeclineSec: num(e.target.value) })} hint="Wer einen Anruf ablehnt, wird dafür erst nach dieser Zeit wieder gerufen. 0 = für diesen Anrufer nie wieder." />
          </div>
        </div>

        <div>
          <h3 className={styles.heading}>Warten</h3>
          <div className={styles.row}>
            <Field label="Längste Wartezeit (Sekunden, 0 = unbegrenzt)" inputMode="numeric" value={String(q.maxWaitSec)} onChange={(e) => set('maxWaitSec', num(e.target.value))} />
            <Field label="Wartende Anrufer höchstens" inputMode="numeric" value={String(q.maxCallers)} onChange={(e) => set('maxCallers', num(e.target.value))} hint="0 = unbegrenzt." />
            <Select label="Wartemusik" value={q.mohClassId ?? ''} onChange={(e) => set('mohClassId', e.target.value || null)} hint="Gruppen legst du unter „Ansagen und Musik“ → „Wartemusik-Gruppen“ an (eine hochgeladene Datei allein reicht nicht).">
              <option value="">Standard</option>
              {dir.data?.moh.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
            <Checkbox label="Anrufer auch warten lassen, wenn niemand angemeldet ist" checked={q.joinEmpty} onChange={(e) => set('joinEmpty', e.target.checked)} />
          </div>
          <div className={styles.stack} style={{ marginTop: 16 }}>
            <SpeechField label="Begrüßung" value={q.settings.welcome} onChange={(v) => setS({ welcome: v as Speech })} />
            <SpeechField
              label="Wiederkehrende Ansage"
              value={q.settings.periodic}
              onChange={(v) => setS({ periodic: v as Speech })}
              variables={[
                { label: 'Position', token: '{{position}}' },
                { label: 'Wartezeit (Zahl)', token: '{{minuten}}' },
                { label: 'Minute/Minuten', token: '{{einheit}}' },
              ]}
              example="Sie sind Anrufer Nummer {{position}}. Die geschätzte Wartezeit beträgt etwa {{minuten}} {{einheit}}."
            />
            {q.settings.periodic && <Field label="Wiederholen alle (Sekunden)" inputMode="numeric" value={String(q.settings.periodicEverySec ?? 60)} onChange={(e) => setS({ periodicEverySec: num(e.target.value) })} />}
            <div className={styles.row}>
              <Field label="Taste zum Verlassen (z. B. 1)" maxLength={1} value={q.settings.exitKey ?? ''} onChange={(e) => setS({ exitKey: e.target.value.replace(/[^0-9*#]/g, '') })} hint="Der Anrufer landet dann beim Ziel „Wenn die Zeit abläuft“." />
              <Field label="Taste für Rückruf (z. B. 2)" maxLength={1} value={q.settings.callbackKey ?? ''} onChange={(e) => setS({ callbackKey: e.target.value.replace(/[^0-9*#]/g, '') })} hint="Statt zu warten wird der Anrufer zurückgerufen." />
            </div>
          </div>
        </div>

        <div>
          <h3 className={styles.heading}>Anrufbeantworter der Warteschlange</h3>
          <p className={styles.muted} style={{ marginBottom: 12 }}>
            Wähle bei „Wenn niemand abnimmt“ das Ziel „Anrufbeantworter einer Warteschlange“. Die Nachrichten sehen alle Mitarbeiter dieser Warteschlange unter „Nachrichten“; per E-Mail bekommen sie die Mitarbeiter und optional die Adresse unten.
          </p>
          <div className={styles.stack}>
            <SpeechField label="Ansage vor dem Signalton" value={q.settings.voicemail?.greeting} onChange={(v) => setS({ voicemail: { ...q.settings.voicemail, greeting: v as Speech } })} />
            <Field label="Zusätzliche E-Mail-Adresse (optional)" type="email" value={q.settings.voicemail?.email ?? ''} onChange={(e) => setS({ voicemail: { ...q.settings.voicemail, email: e.target.value } })} placeholder="info@firma.de" />
          </div>
        </div>

        <div>
          <h3 className={styles.heading}>Wenn niemand abnimmt</h3>
          <div className={styles.stack}>
            <DestinationPicker label="Wenn die Warteschlange voll ist" value={q.overflow} onChange={(d) => set('overflow', d)} allow={DEST_ALLOW} />
            <DestinationPicker label="Wenn die Wartezeit abläuft" value={q.timeoutDestination} onChange={(d) => set('timeoutDestination', d)} allow={DEST_ALLOW} />
          </div>
        </div>

        <div>
          <h3 className={styles.heading}>Mitarbeiter</h3>
          {members.length === 0 && <p className={styles.muted}>Noch niemand eingetragen.</p>}
          <div className={styles.deviceList}>
            {members.map((m, i) => {
              const ext = dir.data?.extensions.find((e) => e.id === m.extensionId);
              return (
                <div key={m.extensionId} className={styles.device}>
                  <div className={styles.deviceInfo}>
                    <span className={styles.deviceName}>
                      {ext?.number} – {ext?.name}
                    </span>
                  </div>
                  <div className={styles.inline}>
                    <Field label="Priorität" aria-label={`Priorität ${ext?.number}`} inputMode="numeric" style={{ width: 90 }} value={String(m.penalty)} onChange={(e) => setMembers((l) => l.map((x, j) => (j === i ? { ...x, penalty: Math.min(20, num(e.target.value)) } : x)))} />
                    <Checkbox label="Immer angemeldet" checked={m.isStatic} onChange={(e) => setMembers((l) => l.map((x, j) => (j === i ? { ...x, isStatic: e.target.checked } : x)))} />
                    <Button variant="secondary" onClick={() => setMembers((l) => l.filter((_, j) => j !== i))}>
                      Entfernen
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12 }}>
            <MultiSearchSelect
              label="Wer gehört zu dieser Warteschlange?"
              values={members.map((m) => m.extensionId)}
              onChange={(ids) => setMembers(ids.map((id) => members.find((m) => m.extensionId === id) ?? { extensionId: id, penalty: 0, isStatic: false }))}
              options={(dir.data?.extensions ?? []).map((e) => ({ value: e.id, label: e.name, hint: e.number }))}
              placeholder="Name oder Nummer suchen …"
            />
            <p className={styles.muted} style={{ marginTop: 6 }}>
              Priorität 0 klingelt zuerst; höhere Zahlen erst, wenn niemand mit niedrigerer Zahl frei ist. Mitarbeiter melden sich mit *45 an und ab, mit *46 in die Pause – oder in „Mein Telefon“.
            </p>
          </div>
        </div>

        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {queue && (
          <div>
            {confirmDelete ? (
              <div className={styles.inline}>
                <span>Warteschlange wirklich löschen?</span>
                <Button variant="secondary" onClick={() => remove.mutate()}>
                  Ja, löschen
                </Button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  Nein
                </Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
                Löschen …
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
